/**
 * Home2 Content Data
 * Static content for the Home2 landing page.
 * 
 * @module data/home2Content
 */

export const HERO_CONTENT = {
  headline: "Is Your Website Builder Blocking Powerful Content Ideas?",
  subheadline: "GoDaddy, Wix, Squarespace, and Shopify make it nearly impossible to add interactive stories, quizzes, and choose-your-own-adventure experiences.",
  solution: "TweePorter changes that.",
  primaryCTA: "Test Your Engagement Boosting Idea Now!",
  secondaryCTA: "See How It Works",
};

export const PROOF_STATS = [
  {
    value: "87%",
    label: "of marketers say interactive content grabs attention better",
    source: "Content Marketing Institute",
    sourceUrl: "https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf",
    icon: "chart",
  },
  {
    value: "4-5 min",
    label: "longer time-on-page vs static content",
    source: "SalesHub.ca",
    sourceUrl: "https://www.saleshub.ca/these-interactive-content-platforms-are-transforming-digital-marketing-results/",
    icon: "clock",
  },
  {
    value: "40-50%",
    label: "conversion rates for quizzes (vs 10-20% static)",
    source: "Outgrow",
    sourceUrl: "https://outgrow.co/why-interactive/",
    icon: "trending",
  },
  {
    value: "73%",
    label: "say interactive content improves message retention",
    source: "Content Marketing Institute",
    sourceUrl: "https://contentmarketinginstitute.com/wp-content/uploads/2017/06/IonInteractive_Symphony_Final.pdf",
    icon: "brain",
  },
];

export const PROBLEM_CONTENT = {
  title: "What This Solves",
  preview: "Why most website builders reject your interactive content...",
  points: [
    { platform: "GoDaddy", issue: "Strips JavaScript from custom HTML blocks" },
    { platform: "Wix", issue: "Blocks external embeds and iframes" },
    { platform: "Squarespace", issue: "Limits code injection locations" },
    { platform: "Shopify", issue: "Restricts interactive elements in product pages" },
  ],
  conclusion: "Traditional solutions require separate hosting, SSL certificates, and CORS configuration. TweePorter bypasses all of this.",
};

export const COMPILER_STEPS = [
  {
    id: "step1",
    number: 1,
    title: "Library",
    icon: "library",
    headline: "Start with Pre-Built Stories or Create Your Own",
    description: "Browse our Sample Gallery of ready-made interactive stories. Pick one to test, or jump straight to creating your own.",
    expandedTitle: "What's in the Library",
    expandedContent: [
      "Interactive fiction templates",
      "Product quiz examples",
      "Customer journey samples",
      "Educational story formats",
    ],
    cta: "Browse Sample Gallery",
    ctaPath: "/sample-gallery",
  },
  {
    id: "step2",
    number: 2,
    title: "Design",
    icon: "palette",
    headline: "Customize Colors, Layout, and Navigation",
    description: "Match your story's appearance to your brand. Choose button colors, background themes, and how readers navigate between sections.",
    expandedTitle: "Design Options",
    expandedContent: [
      "5 color presets (Default, Dark, Nature, Ocean, Sunset)",
      "Custom color pickers for bold, italic, headings, links",
      "Layout options: Side-by-side, stacked, minimal",
      "Button styles: Pill, square, outline",
    ],
    cta: "See Design Options",
    ctaPath: "/compiler",
  },
  {
    id: "step3",
    number: 3,
    title: "Code",
    icon: "code",
    headline: "Write or Paste Your Twee Code",
    description: "Already have a story in Twine? Paste your Twee code directly. New to this? We provide pre-created stories you can test immediately—just click and compile.",
    expandedTitle: "What's Twee Format?",
    expandedContent: [
      "Simple text-based story format",
      "Used by Twine (free tool with 1M+ users)",
      "Each passage starts with :: PassageName",
      "Links use [[Display Text|Target]] syntax",
    ],
    cta: "Open the Compiler",
    ctaPath: "/compiler",
  },
  {
    id: "step4",
    number: 4,
    title: "Export",
    icon: "download",
    headline: "Get Clean HTML That Works Everywhere",
    description: "After you compile, we generate your interactive story as clean HTML. Preview how it looks, download standard HTML for any website, or get WordPress-ready content with one click.",
    expandedTitle: "Export Formats",
    expandedContent: [
      "Preview: See exactly how your story will appear",
      "Standard HTML: Works on GoDaddy, Wix, Squarespace, Shopify",
      "WordPress Block: Copy-paste into any WordPress HTML block",
      "WordPress + JS: Enhanced navigation features",
    ],
    cta: "Start Building",
    ctaPath: "/compiler",
  },
];

export const CTA_FOOTER = {
  headline: "Ready to break free from Flatland?",
  primaryCTA: "Test Your Engagement Boosting Idea Now!",
  secondaryCTA: "Open the Compiler",
  quote: "The wall of text is dead. Long live the story.",
  quoteSource: "The Flatland Trap",
};

export const FAQ_DATA = [
  {
    question: "What website builders does TweePorter work with?",
    answer: "TweePorter works with GoDaddy, Wix, Squarespace, Shopify, Webflow, WordPress (HTML blocks), and any website builder that accepts custom HTML. The output is script-free, so it bypasses restrictions that normally block interactive content."
  },
  {
    question: "Do I need coding experience to use TweePorter?",
    answer: "No coding experience required! If you can use Twine (a free visual tool for creating interactive stories), you can use TweePorter. Simply paste your Twee code and get clean HTML output."
  },
  {
    question: "Is TweePorter free to use?",
    answer: "Yes, TweePorter is free to use. You can compile unlimited stories and use all basic features at no cost."
  },
  {
    question: "What is Twee/SugarCube format?",
    answer: "Twee is a plain-text format for writing interactive fiction, commonly used with Twine. SugarCube is a popular story format. TweePorter is optimized for SugarCube-style Twee code."
  },
  {
    question: "How do I embed the compiled story on my website?",
    answer: "After compiling your story, simply copy the generated HTML and paste it into any HTML block or custom code section on your website builder. The story runs entirely within that HTML block."
  },
  {
    question: "Why can't I just use iframes?",
    answer: "Most website builders block iframes from unknown sources for security reasons. TweePorter generates self-contained HTML that doesn't require iframes or external hosting."
  },
];
